import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  retryCount: number;
}

const MAX_RETRIES = 2;

/**
 * Catches chunk-load failures (lazy imports) and auto-retries by reloading
 * the page once. After MAX_RETRIES it shows a manual-retry fallback.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, retryCount: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const isChunkError =
      /loading chunk|failed to fetch dynamically imported module|import.*failed/i.test(
        error.message
      );

    if (!isChunkError) return;

    const retries = Number(sessionStorage.getItem("chunk-retry") ?? "0");

    if (retries < MAX_RETRIES) {
      sessionStorage.setItem("chunk-retry", String(retries + 1));
      window.location.reload();
    }
  }

  handleRetry = () => {
    sessionStorage.removeItem("chunk-retry");
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 px-6 text-center bg-background"
        >
          <p className="text-lg font-semibold text-foreground">
            Something went wrong loading the page
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            This is usually caused by a network hiccup. Tap below to try again.
          </p>
          <button
            onClick={this.handleRetry}
            className="mt-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition hover:-translate-y-0.5"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
